/**
 * 个股实时行情（WebSocket）状态镜像：扁平快照 + 与 channel 的薄转发。
 *
 * - 业务侧只感知 `fullCodes` 与 `fields`，通过 `dispatch('stock/subscribeCodes', { fullCodes, fields })`
 *   与 `dispatch('stock/unsubscribeCodes', ...)` 与底层交互；
 * - 协议层的 channel 实现、订阅同步、轮询保活、下行解析、per-fullCode 引用计数全部位于
 *   `@/utils/realtime/stockChannels/`，本模块仅负责把 channel 推上来的 patch commit 到 state，
 *   供组件响应式读取。
 * - 退订后 idle 条目保留冻结快照防闪烁，并由 LRU 上限控制条目数量；活跃条目仅缓存订阅声明的字段。
 *
 * 安装时机：channel 采用 **lazy install**——本模块在首次成功的 `subscribeCodes`
 * 时才会拿到 SecChannelSubscriber 单例并触发其 install，避免页面进入即占用 WS 连接与定时器；
 * `unsubscribeCodes` 不会触发安装（无订阅可退）。
 *
 * @module store/modules/stock
 */

import { isNil, isPlainObject } from 'lodash-es';
import { getSecChannelSubscriber } from '@/utils/realtime/stockChannels';
import { LruCache } from '@/utils/lruCache';
import { normalizeStringList } from '@/utils/string';

/** idle 快照的最大保留条数（超出后按 LRU 淘汰） */
export const MAX_IDLE_SNAPSHOTS = 200;

/**
 * fullCode -> (field -> refCount)
 * 字段级引用计数：决定某个 fullCode 的快照允许缓存哪些字段。
 * @type {Map<string, Map<string, number>>}
 */
const fieldRefByFullCode = new Map();

/** idle 快照 LRU：仅存 refCount 已归零但仍保留在 state 的 code */
const idleSnapshotLru = new LruCache({ maxSize: MAX_IDLE_SNAPSHOTS });

/**
 * 确保 `snapshotsByFullCode` 上存在该 code 节点。
 * @param {object} state
 * @param {string} fullCode
 * @returns {Record<string, unknown>}
 */
function ensureSnapshotNode(state, fullCode) {
  if (!state.snapshotsByFullCode[fullCode]) {
    state.snapshotsByFullCode[fullCode] = {};
  }
  return state.snapshotsByFullCode[fullCode];
}

/**
 * 把 patch 扁平合并到 `snapshotsByFullCode[code].quote`：
 * 各 channel 推上来的字段不区分来源、共同合并到同一份 quote 上。
 * @param {object} state
 * @param {string} fullCode
 * @param {object} patch
 */
function mergeOneQuote(state, fullCode, patch) {
  if (!fullCode || !patch || !isPlainObject(patch)) {
    return;
  }
  const fieldRef = fieldRefByFullCode.get(fullCode);
  if (!fieldRef || !fieldRef.size) {
    return;
  }
  const node = ensureSnapshotNode(state, fullCode);
  const prev = isPlainObject(node.quote) ? node.quote : {};
  const next = { ...prev, fullCode };
  // 仅缓存仍被活跃订阅声明的字段，避免把整包推送字段无差别常驻内存。
  for (const [field, refCount] of fieldRef.entries()) {
    if (refCount <= 0) {
      continue;
    }
    if (!isNil(patch[field])) {
      next[field] = patch[field];
    }
  }
  node.quote = next;
}

/**
 * 懒装并取回 SecChannelSubscriber 单例：首次调用时实例化并把回调桥接到本模块的 mutations。
 *
 * `getSecChannelSubscriber` 自身保证幂等——首次实例化时绑定的回调即为整个生命周期内的回调，
 * 后续传入的 callbacks 会被忽略；这里捕获 `ctx.commit` 是安全的——namespaced action 中的 `commit`
 * 由 vuex 在 store 上预先绑定，在 store 生命周期内是稳定引用，不会因 action 重新派发而失效。
 *
 * @param {import('vuex').ActionContext<any, any>} ctx 当前 action 的上下文
 * @returns {SecChannelSubscriber}
 */
function ensureSecChannelSubscriber(ctx) {
  return getSecChannelSubscriber({
    onPatch: (fullCode, patch) => {
      ctx.commit('mergeQuoteRow', { fullCode, patch });
    }
  });
}

/**
 * 提取当前订阅请求里可用于 SEC channel 的字段集合。
 * 当前 `stock` 模块仅接入 SEC channel，因此允许未知字段透传，
 * 以支持 stockDetail 扁平后的扩展字段（如 tradeStatus）按需缓存。
 *
 * @param {string[]} fields
 * @returns {string[]}
 */
function pickCacheFields(fields) {
  const out = [];
  for (const field of fields) {
    const key = String(field || '').trim();
    if (!key) {
      continue;
    }
    // 未知字段也允许透传（后续由 patch 是否包含决定是否入缓存）。
    out.push(key);
  }
  return [...new Set(out)];
}

/**
 * 增加某 fullCode 的字段级引用计数。
 * @param {string} fullCode
 * @param {string[]} fields
 * @returns {void}
 */
function increaseFieldRefs(fullCode, fields) {
  let fieldRef = fieldRefByFullCode.get(fullCode);
  if (!fieldRef) {
    fieldRef = new Map();
    fieldRefByFullCode.set(fullCode, fieldRef);
  }
  for (const field of fields) {
    fieldRef.set(field, (fieldRef.get(field) || 0) + 1);
  }
}

/**
 * 减少某 fullCode 的字段级引用计数。
 * @param {string} fullCode
 * @param {string[]} fields
 * @returns {{ removedFields: string[], becameIdle: boolean }}
 */
function decreaseFieldRefs(fullCode, fields) {
  const fieldRef = fieldRefByFullCode.get(fullCode);
  if (!fieldRef) {
    return { removedFields: [], becameIdle: false };
  }
  const removedFields = [];
  for (const field of fields) {
    const cur = fieldRef.get(field) || 0;
    if (cur <= 0) {
      continue;
    }
    const next = cur - 1;
    if (next <= 0) {
      fieldRef.delete(field);
      removedFields.push(field);
    } else {
      fieldRef.set(field, next);
    }
  }
  if (!fieldRef.size) {
    fieldRefByFullCode.delete(fullCode);
    return { removedFields, becameIdle: true };
  }
  return { removedFields, becameIdle: false };
}

/**
 * 汇总当前缓存控制相关的可观测指标（供 getter / 调试使用）。
 * @param {object} state vuex state
 * @returns {{
 *   activeCodeCount: number,
 *   idleCodeCount: number,
 *   snapshotNodeCount: number,
 *   totalFieldRefCount: number,
 *   maxIdleSnapshots: number
 * }}
 */
function buildCacheStats(state) {
  let totalFieldRefCount = 0;
  for (const fieldRef of fieldRefByFullCode.values()) {
    for (const refCount of fieldRef.values()) {
      if (refCount > 0) {
        totalFieldRefCount += refCount;
      }
    }
  }
  return {
    activeCodeCount: fieldRefByFullCode.size,
    idleCodeCount: idleSnapshotLru.size,
    snapshotNodeCount: Object.keys(state.snapshotsByFullCode).length,
    totalFieldRefCount,
    maxIdleSnapshots: idleSnapshotLru.maxSize
  };
}

export default {
  namespaced: true,
  state: () => ({
    /**
     * fullCode -> { quote: { ... } } 扁平合并快照，不区分来源 channel。
     * 约定：只允许按 key 增删 / 字段级 patch，禁止整体替换该对象（`state.snapshotsByFullCode = {}`）——
     * 消费方（如 useWatchlistQuoteSort）依赖「顶层 key 变化可追踪、子属性变化可绕过追踪」做节流重排，
     * 整体替换会静默破坏该语义。
     * @type {Record<string, { quote?: Record<string, unknown> }>}
     */
    snapshotsByFullCode: {}
  }),

  getters: {
    /**
     * 缓存控制汇总指标，便于 Vue DevTools / 控制台排查内存占用。
     * @param {object} state
     * @returns {ReturnType<typeof buildCacheStats>}
     */
    cacheStats(state) {
      return buildCacheStats(state);
    },

    /** 当前仍有字段级订阅的 fullCode 数量 */
    activeSnapshotCount(_state, getters) {
      return getters.cacheStats.activeCodeCount;
    },

    /** 当前 idle LRU 中的 fullCode 数量 */
    idleSnapshotCount(_state, getters) {
      return getters.cacheStats.idleCodeCount;
    },

    /** state 中快照节点总数（活跃 + idle） */
    cachedSnapshotCount(_state, getters) {
      return getters.cacheStats.snapshotNodeCount;
    },

    /** 字段级引用计数总和 */
    totalFieldRefCount(_state, getters) {
      return getters.cacheStats.totalFieldRefCount;
    },

    /** idle 快照 LRU 上限 */
    maxIdleSnapshots(_state, getters) {
      return getters.cacheStats.maxIdleSnapshots;
    }
  },

  mutations: {
    /**
     * 单条 patch 扁平合并到快照 `.quote`（由 channel.onPatch 推送）。
     * @param {object} state
     * @param {{ fullCode: string, patch: object }} payload
     */
    mergeQuoteRow(state, { fullCode, patch }) {
      mergeOneQuote(state, fullCode, patch);
    },

    /**
     * 显式移除某 fullCode 的快照节点（退订不会自动调用，仅用于需要主动清缓存的场景）。
     * @param {object} state
     * @param {string} fullCode
     */
    removeFullCodeNode(state, fullCode) {
      const code = String(fullCode || '').trim();
      if (!code) {
        return;
      }
      fieldRefByFullCode.delete(code);
      idleSnapshotLru.delete(code);
      delete state.snapshotsByFullCode[code];
    },

    /**
     * 从某 fullCode 的 quote 中裁剪指定字段（仅在该 code 仍活跃时执行）。
     * @param {object} state
     * @param {{ fullCode: string, fields: string[] }} payload
     */
    pruneQuoteFields(state, { fullCode, fields }) {
      const code = String(fullCode || '').trim();
      if (!code || !Array.isArray(fields) || !fields.length) {
        return;
      }
      const node = state.snapshotsByFullCode[code];
      if (!node || !isPlainObject(node.quote)) {
        return;
      }
      for (const field of fields) {
        if (field in node.quote) {
          delete node.quote[field];
        }
      }
    }
  },

  actions: {
    /**
     * 增加订阅：根据 fields 反查命中的 channel，转发到对应的 subscriber 实例。
     * `fields` 必传，缺省时本次调用会被忽略并打 warn，避免出现「订阅了但不知道订阅什么」的隐式行为。
     * @param {import('vuex').ActionContext<any, any>} ctx
     * @param {{ fullCodes: string[], fields: string[] }} payload
     * @returns {void}
     */
    subscribeCodes(ctx, { fullCodes, fields } = {}) {
      const codes = normalizeStringList(fullCodes);
      const fs = normalizeStringList(fields);
      if (!codes.length) {
        return;
      }
      if (!fs.length) {
        // fields 必传：缺省视为业务侧 bug，发出 warn 让上层尽快发现
        console.warn('[stock] subscribeCodes ignored: fields is required');
        return;
      }
      const cacheFields = pickCacheFields(fs);
      if (!cacheFields.length) {
        console.warn('[stock] subscribeCodes ignored: cache fields is empty', fs);
        return;
      }
      for (const code of codes) {
        increaseFieldRefs(code, cacheFields);
      }
      idleSnapshotLru.deleteMany(codes);
      // 当前 `stock` 模块仅对接 SEC channel；字段裁剪由模块侧完成。
      ensureSecChannelSubscriber(ctx).subscribe(codes);
    },

    /**
     * 减少订阅：根据 fields 反查命中的 channel 并转发；channel 自身在引用计数归零时会主动 destroy。
     * 同样要求 `fields` 必传，且与 subscribe 时一致，否则可能漏退某些 channel 的引用计数。
     * @param {import('vuex').ActionContext<any, any>} ctx
     * @param {{ fullCodes: string[], fields: string[] }} payload
     * @returns {void}
     */
    unsubscribeCodes(ctx, { fullCodes, fields } = {}) {
      const codes = normalizeStringList(fullCodes);
      const fs = normalizeStringList(fields);
      if (!codes.length) {
        return;
      }
      if (!fs.length) {
        console.warn('[stock] unsubscribeCodes ignored: fields is required');
        return;
      }
      const cacheFields = pickCacheFields(fs);
      if (!cacheFields.length) {
        return;
      }
      // 取单例（必要时实例化）；未订阅过则 unsubscribe 内的引用计数判断会自动 no-op
      ensureSecChannelSubscriber(ctx).unsubscribe(codes);
      for (const code of codes) {
        const { removedFields, becameIdle } = decreaseFieldRefs(code, cacheFields);
        // 该 code 仍活跃时才裁字段；若已 idle，保留冻结快照防止短时间回看闪空。
        if (!becameIdle && removedFields.length) {
          ctx.commit('pruneQuoteFields', { fullCode: code, fields: removedFields });
        }
        if (becameIdle && ctx.state.snapshotsByFullCode[code]) {
          idleSnapshotLru.touch(code, Date.now());
        }
      }
      ctx.dispatch('enforceIdleCacheLimit');
    },

    /**
     * 对 idle 快照执行 LRU 控制，避免滚动浏览后缓存条目无限增长。
     * @param {import('vuex').ActionContext<any, any>} ctx
     */
    enforceIdleCacheLimit(ctx) {
      const evictedCodes = idleSnapshotLru.evictOverflow();
      for (const code of evictedCodes) {
        ctx.commit('removeFullCodeNode', code);
      }
    }
  }
};
